import AppsIcon from "@mui/icons-material/Apps";
import MessageIcon from "@mui/icons-material/Message";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
// import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { colors } from "../../../../theme/muiColors";
import { useState } from "react";
import { GlobalSearchModal } from "../../../shared/components/global-search/GlobalSearchModal";
import { Contact } from "../../../internal-contacts/_types/internalContacts.types";
import { JobChatSummary } from "../../../job-chats";
import type { MessageGroupSummary } from "../../../message-groups/messageGroups.types";
import { SmsContact } from "../contacts/types/SmsContact";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";
import { MobileLineSelector } from "./MobileLineSelector";
import { SendNewAllToContact } from "./SendNewAllToContact";
import { SendNewGroupMessage } from "./SendNewGroupMessage";
import { SendNewInternalMessage } from "./SendNewInternalMessage";
import { SendNewSmsToContact } from "./SendNewSmsToContact";

export type ConversationType = "all" | "sms" | "internal" | "events" | "groups";

const tabSx = (selectedColor: string) => ({
  minHeight: 0,
  minWidth: 0,
  px: 2,
  py: 0.2,
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  color: "text.secondary",
  "&.Mui-selected": { color: selectedColor },
  "& .MuiTab-iconWrapper": { mr: 0.5 },
});

const renderSearchInput = (params: any, placeholder: string, color: string) => (
  <TextField
    {...params}
    variant="standard"
    placeholder={placeholder}
    InputProps={{
      ...params.InputProps,
      disableUnderline: true,
      startAdornment: (
        <>
          <Typography sx={{ mr: 1.5, fontSize: 11, fontWeight: 600, textTransform: "uppercase", color }}>To</Typography>
          {params.InputProps.startAdornment}
        </>
      ),
    }}
    sx={{
      "& .MuiInputBase-root": { px: 2, py: 1, fontSize: 13 },
      "& input::placeholder": { color: "var(--fx-surface-textsecondary, #64748B)", opacity: 1 },
    }}
  />
);

export const SendNewSms = ({
  isSmsAvaileble,
  selectedSmsContact,
  selectedInternalContact,
  selectedJobChat,
  handleSelectSmsContact,
  handleSelectInternalContact,
  handleSelectJobChat,
  handleSelectGroup,
  internalContacts,
  smsContacts,
  eventChannels,
  messageGroups,
  canCreateGroups,
  onCreateGroup,
  onCreateSmsGroup,
  contactType,
  onContactTypeChange,
}: {
  selectedSmsContact: SmsContact | null;
  selectedInternalContact: Contact | null;
  selectedJobChat: JobChatSummary | null;
  handleSelectSmsContact: (contact: SmsContact | null) => void;
  handleSelectInternalContact: (contact: Contact | null) => void;
  handleSelectJobChat: (chat: JobChatSummary | null) => void;
  handleSelectGroup: (group: MessageGroupSummary | null) => void;
  internalContacts: Contact[];
  smsContacts: SmsContact[];
  eventChannels: JobChatSummary[];
  messageGroups: MessageGroupSummary[];
  canCreateGroups: boolean;
  onCreateGroup: () => void;
  onCreateSmsGroup: () => void;
  isSmsAvaileble: boolean;
  contactType: ConversationType;
  onContactTypeChange: (type: ConversationType) => void;
}) => {
  const { theme } = useContactCenterTheme();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const hasSelectedContact = selectedSmsContact || selectedInternalContact || selectedJobChat;

  if (hasSelectedContact) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.shell.headerBg,
        borderBottom: "1px solid",
        borderColor: theme.shell.border,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Tabs
            value={contactType}
            onChange={(_, value: ConversationType) => onContactTypeChange(value)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: contactType === "internal" || contactType === "groups" ? colors.blue[500] : theme.shell.accent,
                height: 2,
              },
            }}
          >
            <Tab value="all" icon={<AppsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="All" sx={tabSx(theme.shell.accent)} />
            {isSmsAvaileble && (
              <Tab value="sms" icon={<SmsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="MOBILE" sx={tabSx(theme.shell.accent)} />
            )}
            <Tab value="internal" icon={<MessageIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Internal" sx={tabSx(colors.blue[500])} />
            {/* <Tab value="groups" icon={<GroupsRoundedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Groups" sx={tabSx(colors.blue[500])} /> */}
            <Tab value="events" icon={<WorkOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="JOBS" sx={tabSx(theme.shell.accent)} />
          </Tabs>

          <Tooltip title="Search messages" arrow>
            <IconButton
              size="small"
              aria-label="Search messages"
              onClick={() => setIsSearchOpen(true)}
              sx={{ color: "text.secondary", flexShrink: 0 }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {contactType === "sms" && <MobileLineSelector />}

        {contactType === "all" ? (
          <SendNewAllToContact
            internalContacts={internalContacts}
            smsContacts={smsContacts}
            eventChannels={eventChannels}
            isSmsAvaileble={isSmsAvaileble}
            onSelectInternalContact={handleSelectInternalContact}
            onSelectJobChat={handleSelectJobChat}
            onSelectSmsContact={handleSelectSmsContact}
          />
        ) : contactType === "sms" ? (
          <SendNewSmsToContact smsContacts={smsContacts} onSelectContact={handleSelectSmsContact} />
        ) : contactType === "internal" ? (
          <SendNewInternalMessage
            allContacts={internalContacts}
            groups={messageGroups}
            onSelectContact={handleSelectInternalContact}
            onSelectGroup={handleSelectGroup}
          />
        ) : contactType === "groups" ? (
          <SendNewGroupMessage
            groups={messageGroups}
            canCreateGroups={canCreateGroups}
            onCreateGroup={onCreateGroup}
            onCreateSmsGroup={onCreateSmsGroup}
            onSelectGroup={handleSelectGroup}
          />
        ) : (
          <Autocomplete
            size="small"
            options={eventChannels}
            popupIcon={<SendIcon sx={{ fontSize: 15, color: theme.shell.accent, opacity: 0.5 }} />}
            getOptionLabel={(channel) => channel.alias || `Job #${channel.jobId}`}
            isOptionEqualToValue={(option, value) => option.jobEventUid === value.jobEventUid}
            onChange={(_, channel) => handleSelectJobChat(channel)}
            noOptionsText="No jobs available"
            renderInput={(params) => renderSearchInput(params, "Search jobs...", theme.shell.accent)}
          />
        )}
      </Box>

      <GlobalSearchModal open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </Box>
  );
};
